import React from "react";
import { motion } from "framer-motion";

import {
  ArrowRight,
  PhoneCall,
} from "lucide-react";

import { Link } from "react-router-dom";

export default function CTASection() {
  return (
    <section
      className="
        relative
        py-20 sm:py-24 md:py-32
        overflow-hidden
      "
    >
      {/* BACKGROUND */}

      <div
        className="
          absolute inset-0
          bg-gradient-to-br
          from-[#F7F2FA]
          via-white
          to-[#F3E6F7]
        "
      />

      {/* TOP GLOW */}

      <div
        className="
          absolute top-0 left-1/2
          -translate-x-1/2
          w-[700px] sm:w-[900px]
          h-[350px] sm:h-[500px]
          bg-purple-300/20
          blur-[120px]
          rounded-full
          animate-pulse
        "
      />

      {/* BOTTOM GLOW */}

      <div
        className="
          absolute bottom-0 right-0
          w-[300px] sm:w-[500px]
          h-[300px] sm:h-[500px]
          bg-fuchsia-400/10
          blur-[120px]
          rounded-full
        "
      />

      {/* FLOATING ORB */}

      <div
        className="
          absolute
          top-24 left-10
          w-32 h-32
          bg-purple-400/10
          rounded-full
          blur-3xl
          animate-bounce
          hidden lg:block
        "
      />

      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 md:px-8">
        <motion.div
          initial={{
            opacity: 0,
            y: 40,
          }}
          whileInView={{
            opacity: 1,
            y: 0,
          }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}

          className="
            relative overflow-hidden
            text-center

            bg-white/70
            backdrop-blur-2xl

            border border-purple-200/40

            rounded-[28px]
            sm:rounded-[36px]
            md:rounded-[42px]

            p-6
            sm:p-10
            md:p-16
            lg:p-20

            shadow-[0_25px_80px_rgba(120,70,180,0.15)]

            transition-all duration-500
          "
        >
          {/* INNER GLOW */}

          <div
            className="
              absolute inset-0
              bg-gradient-to-br
              from-purple-500/[0.04]
              via-transparent
              to-fuchsia-500/[0.05]
              pointer-events-none
            "
          />

          {/* SHINE EFFECT */}

          <div
            className="
              absolute inset-0
              opacity-30
              bg-[linear-gradient(120deg,transparent,rgba(255,255,255,0.45),transparent)]
              translate-x-[-100%]
              animate-[shine_6s_linear_infinite]
            "
          />

          {/* BADGE */}

          <div
            className="
              inline-flex items-center gap-2

              px-5 sm:px-6
              py-2 sm:py-2.5

              rounded-full

              bg-purple-100/80

              border border-purple-200

              text-[10px] sm:text-xs
              uppercase
              tracking-[0.25em] sm:tracking-[0.32em]

              text-purple-700
              font-semibold

              mb-6 sm:mb-8
            "
          >
            Let’s Build Together
          </div>

          {/* HEADING */}

          <h2
            className="
              text-3xl
              sm:text-4xl
              md:text-5xl
              lg:text-6xl

              font-bold
              text-slate-900

              leading-[1.1]
              tracking-tight
            "
          >
            Ready to build the future
            <br />

            <span className="relative inline-block">
              <span
                className="
                  bg-gradient-to-r
                  from-[#6D3B8C]
                  via-[#A24FA8]
                  to-[#C67BCF]

                  bg-clip-text
                  text-transparent
                "
              >
                with CodeFix Solutions?
              </span>

              {/* TEXT GLOW */}

              <span
                className="
                  absolute inset-x-0 bottom-2
                  h-5
                  bg-purple-400/30
                  blur-2xl
                  -z-10
                "
              />
            </span>
          </h2>

          {/* DESCRIPTION */}

          <p
            className="
              mt-6 sm:mt-8

              text-base
              sm:text-lg
              md:text-xl

              text-slate-600
              leading-relaxed

              max-w-3xl
              mx-auto
            "
          >
            Partner with us for modern IT solutions,
            AI systems, cloud engineering, staffing,
            and digital transformation services.
          </p>

          {/* BUTTONS */}

          <div
            className="
              mt-10 sm:mt-12

              flex flex-col
              sm:flex-row

              items-center
              justify-center

              gap-4 sm:gap-5
            "
          >
            {/* PRIMARY */}

            <Link
              to="/contact"

              className="
                group

                w-full sm:w-auto

                inline-flex items-center justify-center gap-2

                px-7 sm:px-8
                py-4

                rounded-2xl

                bg-gradient-to-r
                from-[#6D3B8C]
                via-[#9B4D96]
                to-[#B86BC5]

                text-white
                font-semibold

                shadow-lg
                shadow-purple-500/25

                hover:scale-[1.03]
                active:scale-[0.98]

                hover:shadow-purple-500/40

                transition-all duration-300
              "
            >
              Get Started

              <ArrowRight
                className="
                  w-4 h-4
                  transition-transform duration-300
                  group-hover:translate-x-1
                "
              />
            </Link>

            {/* SECONDARY */}

            <Link
              to="/contact?type=consultation"

              className="
                w-full sm:w-auto

                inline-flex items-center justify-center gap-2

                px-7 sm:px-8
                py-4

                rounded-2xl

                bg-white/80
                backdrop-blur-md

                border border-purple-200

                text-slate-700
                font-semibold

                hover:bg-purple-50
                hover:border-purple-300
                hover:text-purple-700

                active:scale-[0.98]

                transition-all duration-300
              "
            >
              <PhoneCall className="w-4 h-4" />

              Book Consultation
            </Link>
          </div>

          {/* FOOTER */}

          <div
            className="
              mt-10 sm:mt-14

              text-[10px] sm:text-xs
              uppercase

              tracking-[0.2em] sm:tracking-[0.35em]

              text-slate-500
              leading-relaxed
            "
          >
            Trusted by startups · enterprises · global teams
          </div>
        </motion.div>
      </div>
    </section>
  );
}